/**
 * Automatic page_view capture: fires once for the initial load, then on
 * every SPA navigation (history.pushState / replaceState / popstate).
 */
export interface PageviewCapture {
  /** Queue a navigation still waiting for its title, with the title it has now. */
  settle(): void;
  /** Settle, then restore the patched history methods. */
  uninstall(): void;
}

export const NO_CAPTURE: PageviewCapture = { settle() {}, uninstall() {} };

interface PageviewClient {
  page(): void;
  beginPageview(referrer: string): (title: string | undefined) => void;
}

/**
 * How long a navigation waits for the new route's `<title>`. Routers update
 * history before they render, and some (Next.js App Router) fetch the route
 * first, so the title can land well after the URL. A page whose title never
 * changes goes out with it when this runs out.
 */
export const TITLE_WAIT_MS = 2000;

interface Pending {
  commit: (title: string | undefined) => void;
  /** The title when the navigation started: the page being left's. */
  staleTitle: string;
  timer: ReturnType<typeof setTimeout>;
  observer: MutationObserver | undefined;
}

export function installPageviewCapture(client: PageviewClient): PageviewCapture {
  if (typeof window === 'undefined' || typeof history === 'undefined') {
    return NO_CAPTURE;
  }

  let pending: Pending | undefined;

  const finish = (title: string | undefined) => {
    if (!pending) return;
    const { commit, timer, observer } = pending;
    pending = undefined;
    clearTimeout(timer);
    observer?.disconnect();
    commit(title);
  };

  // Superseded or cut short: the title it has now, unless that's still the
  // previous page's (then none: a wrong title is worse than a missing one).
  const settle = () => {
    if (pending) finish(document.title === pending.staleTitle ? undefined : document.title);
  };

  // Routers call replaceState for state-only updates (scroll position,
  // redirects to the same URL): count a page view only when the URL changes.
  let lastHref = location.href;
  const onNavigate = () => {
    if (location.href === lastHref) return;
    settle();
    const commit = client.beginPageview(lastHref);
    lastHref = location.href;
    const staleTitle = document.title;
    // shortcut: no observer (an odd DOM shim) just means the timer decides.
    const observer =
      typeof MutationObserver === 'undefined'
        ? undefined
        : new MutationObserver(() => {
            if (document.title !== staleTitle) finish(document.title);
          });
    observer?.observe(document.head ?? document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const timer = setTimeout(() => finish(document.title), TITLE_WAIT_MS);
    pending = { commit, staleTitle, timer, observer };
  };

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args: Parameters<History['pushState']>) {
    originalPushState.apply(this, args);
    onNavigate();
  };
  history.replaceState = function (...args: Parameters<History['replaceState']>) {
    originalReplaceState.apply(this, args);
    onNavigate();
  };
  window.addEventListener('popstate', onNavigate);

  client.page();

  return {
    settle,
    uninstall() {
      settle();
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', onNavigate);
    },
  };
}
